// src/features/dashboard/pages/UserDashboardPage.tsx

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
} from 'react';

import {
  useDispatch,
} from 'react-redux';

import {
  useNavigate,
} from 'react-router-dom';

import {
  CalendarDays,
  ChevronDown,
  ChevronRight,
  CircleUserRound,
  Gift,
  History,
  House,
  LogOut,
  Medal,
  Sparkles,
  Ticket,
  Trophy,
} from 'lucide-react';

import type {
  AppDispatch,
} from '../../../app/store';

import {
  clearTokens,
} from '../../auth/authSlice';

import {
  loginService,
} from '../../auth/login/services/LoginService';

import {
  userDashboardService,
} from '../services/userDashboardService';

import type {
  DashboardUser,
} from '../types/userDashboard.types';

import RouteConfig
  from '../../../routes/RouteConfig';


type MenuTone =
  | 'gold'
  | 'mint'
  | 'blue'
  | 'purple'
  | 'rose'
  | 'gray';


type DashboardMenuItem = {
  id: string;

  label: string;

  description: string;

  icon: ComponentType<{
    size?: number;
    strokeWidth?: number;
    className?: string;
  }>;

  path: string;

  badge?: string;

  tone?: MenuTone;
};


const MENU_TONE_CLASSES:
  Record<
    MenuTone,
    {
      icon: string;
      badge: string;
      decoration: string;
    }
  > = {
    gold: {
      icon:
        'bg-amber-50 text-amber-600',

      badge:
        'bg-amber-50 text-amber-700 ring-amber-200',

      decoration:
        'bg-amber-400',
    },

    mint: {
      icon:
        'bg-emerald-50 text-emerald-600',

      badge:
        'bg-emerald-50 text-emerald-700 ring-emerald-200',

      decoration:
        'bg-emerald-400',
    },

    blue: {
      icon:
        'bg-blue-50 text-blue-600',

      badge:
        'bg-blue-50 text-blue-700 ring-blue-200',

      decoration:
        'bg-blue-400',
    },

    purple: {
      icon:
        'bg-violet-50 text-violet-600',

      badge:
        'bg-violet-50 text-violet-700 ring-violet-200',

      decoration:
        'bg-violet-400',
    },

    rose: {
      icon:
        'bg-rose-50 text-rose-600',

      badge:
        'bg-rose-50 text-rose-700 ring-rose-200',

      decoration:
        'bg-rose-400',
    },

    gray: {
      icon:
        'bg-zinc-100 text-zinc-600',

      badge:
        'bg-zinc-100 text-zinc-700 ring-zinc-200',

      decoration:
        'bg-zinc-400',
    },
  };


const formatNumber = (
  value: number,
): string => {
  return new Intl.NumberFormat(
    'ko-KR',
  ).format(
    value,
  );
};


const getDisplayName = (
  user: DashboardUser,
): string => {
  return (
    user.userNickname?.trim() ||
    user.userName?.trim() ||
    user.userId ||
    '회원'
  );
};


export default function UserDashboardPage():
  React.JSX.Element {
  const navigate =
    useNavigate();

  const dispatch =
    useDispatch<AppDispatch>();


  const [
    user,
    setUser,
  ] =
    useState<DashboardUser | null>(
      null,
    );


  const [
    selectedStoreId,
    setSelectedStoreId,
  ] =
    useState<number | null>(
      null,
    );


  const [
    isLoading,
    setIsLoading,
  ] =
    useState(true);


  const [
    errorMessage,
    setErrorMessage,
  ] =
    useState('');


  const [
    isLoggingOut,
    setIsLoggingOut,
  ] =
    useState(false);


  /**
   * =========================================================
   * 사용자 대시보드 조회
   * =========================================================
   */
  const loadUser =
    useCallback(
      async (): Promise<void> => {
        try {
          setIsLoading(
            true,
          );

          setErrorMessage(
            '',
          );


          const {
            user:
              dashboardUser,
          } =
            await userDashboardService
              .getDashboard();


          setUser(
            dashboardUser,
          );


          setSelectedStoreId(
            dashboardUser.storeId ??
              dashboardUser
                .pointList[
                  0
                ]?.storeId ??
              null,
          );

        } catch (
          error: unknown
        ) {
          console.error(
            '대시보드 조회 실패:',
            error,
          );


          setUser(
            null,
          );


          setErrorMessage(
            '회원 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
          );

        } finally {
          setIsLoading(
            false,
          );
        }
      },
      [],
    );


  useEffect(
    () => {
      void loadUser();
    },
    [
      loadUser,
    ],
  );


  /**
   * =========================================================
   * 로그아웃
   * =========================================================
   */
  const handleLogout =
    useCallback(
      async (): Promise<void> => {
        if (
          isLoggingOut
        ) {
          return;
        }


        setIsLoggingOut(
          true,
        );


        try {
          await loginService
            .logout();

        } catch (
          error: unknown
        ) {
          console.error(
            '로그아웃 요청 실패:',
            error,
          );

        } finally {
          dispatch(
            clearTokens(),
          );


          navigate(
            RouteConfig
              .Auth
              .Login
              .fullPath,
            {
              replace:
                true,
            },
          );
        }
      },
      [
        dispatch,
        isLoggingOut,
        navigate,
      ],
    );


  /**
   * =========================================================
   * 선택 매장 포인트
   * =========================================================
   */
  const selectedStorePoint =
    useMemo(
      () => {
        if (
          !user ||
          user.pointList.length ===
            0
        ) {
          return null;
        }


        return (
          user.pointList.find(
            (
              item,
            ) =>
              item.storeId ===
              selectedStoreId,
          ) ??
          user.pointList.find(
            (
              item,
            ) =>
              item.storeId ===
              user.storeId,
          ) ??
          user.pointList[
            0
          ] ??
          null
        );
      },
      [
        user,
        selectedStoreId,
      ],
    );


  /**
   * =========================================================
   * Quick Menu
   * =========================================================
   */
  const menuItems =
    useMemo<
      DashboardMenuItem[]
    >(
      () => [
        {
          id:
            'tournaments',

          label:
            '대회',

          description:
            '진행 예정 대회와 참가 정보를 확인합니다.',

          icon:
            Trophy,

          path:
            RouteConfig
              .User
              .Tournaments
              .fullPath,

          badge:
            'LIVE',

          tone:
            'gold',
        },

        {
          id:
            'ranking',

          label:
            '랭킹',

          description:
            '월간 순위와 리더보드를 확인합니다.',

          icon:
            Medal,

          path:
            RouteConfig
              .User
              .Ranking
              .fullPath,

          tone:
            'blue',
        },

        {
          id:
            'attendance',

          label:
            '출석',

          description:
            '출석 현황과 달성 보상을 확인합니다.',

          icon:
            CalendarDays,

          path:
            RouteConfig
              .Common
              .ComingSoon
              .fullPath,

          tone:
            'purple',
        },

        {
          id:
            'coupons',

          label:
            '쿠폰',

          description:
            '사용 가능한 쿠폰과 혜택을 확인합니다.',

          icon:
            Gift,

          path:
            RouteConfig
              .User
              .Coupons
              .fullPath,

          tone:
            'rose',
        },
      ],
      [],
    );


  /**
   * =========================================================
   * Loading
   * =========================================================
   */
  if (
    isLoading
  ) {
    return (
      <main className="grid min-h-screen place-items-center bg-gray-50 px-4 text-center text-gray-700">

        <div>

          <div
            className="mx-auto h-9 w-9 animate-spin rounded-full border-[3px] border-gray-200 border-t-gray-900"
            role="status"
            aria-label="대시보드 불러오는 중"
          />


          <p className="mt-3 text-sm">
            회원 정보를 불러오고 있습니다.
          </p>

        </div>

      </main>
    );
  }


  /**
   * =========================================================
   * Error
   * =========================================================
   */
  if (
    errorMessage ||
    !user
  ) {
    return (
      <main className="grid min-h-screen place-items-center bg-gray-50 px-4">

        <section className="w-full max-w-md rounded-3xl border border-gray-200 bg-white p-7 text-center shadow-xl shadow-gray-200/60">

          <span className="mx-auto grid h-11 w-11 place-items-center rounded-full bg-red-100 font-black text-red-600">
            !
          </span>


          <h1 className="mt-4 text-xl font-bold text-gray-900">
            정보를 불러올 수 없습니다
          </h1>


          <p className="mt-2 text-sm leading-6 text-gray-500">
            {errorMessage ||
              '회원 정보가 없습니다.'}
          </p>


          <button
            type="button"
            onClick={() =>
              void loadUser()
            }
            className="mt-5 h-11 w-full rounded-xl bg-gray-900 text-sm font-bold text-white transition hover:bg-gray-800 active:scale-[0.99]"
          >
            다시 시도
          </button>

        </section>

      </main>
    );
  }


  const displayName =
    getDisplayName(
      user,
    );


  return (
    <>
      {/* =====================================================
          Dashboard
          ===================================================== */}
      <main className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(240,184,72,0.12),transparent_30%),linear-gradient(180deg,#f8f9fb_0%,#f3f5f7_100%)] p-3 text-gray-900 sm:p-4 lg:p-5">

        <section className="mx-auto grid min-h-[calc(100vh-24px)] w-full max-w-[1440px] grid-rows-[auto_auto_minmax(0,1fr)] gap-3 sm:min-h-[calc(100vh-32px)] sm:gap-4 lg:min-h-[calc(100vh-40px)]">


          {/* =================================================
              Header
              ================================================= */}
          <header className="flex items-start justify-between gap-3 px-0.5 py-1 lg:items-center lg:gap-6">

            <div className="min-w-0">

              <span className="inline-flex items-center gap-1.5 text-xs font-extrabold tracking-[0.12em] text-amber-700">

                <Sparkles
                  size={14}
                />

                LAPOIT MEMBER

              </span>


              <h1 className="mt-1.5 truncate text-2xl font-medium tracking-[-0.04em] text-gray-900 sm:text-3xl lg:text-4xl">

                <strong className="font-black">
                  {displayName}
                </strong>

                님, 반갑습니다.

              </h1>

            </div>


            <div className="flex shrink-0 items-center gap-2">

              {/* 프로필 */}
              <button
                type="button"
                onClick={() =>
                  navigate(
                    RouteConfig
                      .User
                      .Profile
                      .fullPath,
                  )
                }
                className="grid h-11 w-11 place-items-center rounded-2xl border border-gray-200 bg-white/90 text-gray-700 shadow-lg shadow-gray-200/40 transition hover:-translate-y-0.5 hover:bg-gray-50 hover:text-gray-900 hover:shadow-xl active:scale-95 sm:h-12 sm:w-12"
                aria-label="프로필 관리"
              >

                <CircleUserRound
                  size={24}
                  strokeWidth={1.9}
                />

              </button>


              {/* 로그아웃 */}
              <button
                type="button"
                onClick={() =>
                  void handleLogout()
                }
                disabled={
                  isLoggingOut
                }
                className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white/90 px-3.5 text-sm font-bold text-gray-600 shadow-lg shadow-gray-200/40 transition hover:-translate-y-0.5 hover:border-red-200 hover:bg-red-50 hover:text-red-600 hover:shadow-xl active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 sm:h-12 sm:px-4"
              >

                <LogOut
                  size={19}
                  strokeWidth={2}
                />


                <span className="hidden sm:inline">
                  {isLoggingOut
                    ? '로그아웃 중'
                    : '로그아웃'}
                </span>

              </button>

            </div>

          </header>


          {/* =================================================
              Summary
              ================================================= */}
          <section className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3">

            {/* 보유 시드 */}
            <article className="col-span-2 flex min-w-0 items-center gap-3 rounded-2xl border border-gray-900 bg-gray-900 px-4 py-3.5 text-white shadow-lg shadow-gray-300/40 sm:col-span-1 sm:px-5 sm:py-4">

              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/10 text-amber-300">

                <Ticket
                  size={21}
                />

              </span>


              <div className="min-w-0">

                <span className="text-xs font-bold text-gray-300">
                  보유 시드
                </span>


                <strong className="mt-1 block truncate text-2xl leading-none">

                  {formatNumber(
                    user.point ??
                      0,
                  )}


                  <small className="ml-1 text-xs font-semibold text-gray-400">
                    시드
                  </small>

                </strong>

              </div>

            </article>


            {/* 매장 이용권 */}
            <article className="flex min-w-0 items-center gap-3 rounded-2xl border border-gray-200 bg-white/95 px-3.5 py-3.5 shadow-lg shadow-gray-200/40 sm:px-5 sm:py-4">

              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gray-100 text-gray-600">

                <House
                  size={21}
                />

              </span>


              <div className="min-w-0 flex-1">

                <span className="text-xs font-bold text-gray-500">
                  매장이용권
                </span>


                {user.pointList.length >
                  0 &&
                selectedStorePoint ? (

                  <>

                    <div className="relative mt-1">

                      <select
                        value={
                          selectedStorePoint
                            .storeId
                        }
                        onChange={(
                          event,
                        ) =>
                          setSelectedStoreId(
                            Number(
                              event
                                .target
                                .value,
                            ),
                          )
                        }
                        className="w-full cursor-pointer appearance-none truncate rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-2 pr-7 text-xs font-bold text-gray-700 outline-none"
                      >

                        {user.pointList.map(
                          (
                            item,
                          ) => (

                            <option
                              key={
                                item.storeId
                              }
                              value={
                                item.storeId
                              }
                            >
                              {
                                item.storeName
                              }
                            </option>

                          ),
                        )}

                      </select>


                      <ChevronDown
                        size={14}
                        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                      />

                    </div>


                    <strong className="mt-2 block truncate text-xl leading-none sm:text-2xl">

                      {formatNumber(
                        selectedStorePoint
                          .point ??
                          0,
                      )}


                      <small className="ml-1 text-xs font-semibold text-gray-400">
                        P
                      </small>

                    </strong>

                  </>

                ) : (

                  <>

                    <strong className="mt-1 block text-xl">

                      0

                      <small className="ml-1 text-xs text-gray-400">
                        P
                      </small>

                    </strong>


                    <span className="mt-1 block text-[10px] text-gray-400">
                      이용 가능한 매장이 없습니다.
                    </span>

                  </>

                )}

              </div>

            </article>


            {/* 이용 내역 */}
            <button
              type="button"
              onClick={() =>
                navigate(
                  RouteConfig
                    .User
                    .History
                    .fullPath,
                )
              }
              className="flex w-full items-center gap-3 rounded-2xl border border-gray-200 bg-white/95 px-3.5 py-3.5 text-left shadow-lg shadow-gray-200/40 transition hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-xl sm:px-5 sm:py-4"
            >

              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gray-100 text-gray-600">

                <History
                  size={21}
                />

              </span>


              <div className="min-w-0 flex-1">

                <span className="block text-xs font-bold text-gray-500">
                  최근 이용
                </span>


                <strong className="mt-1 block truncate text-sm sm:text-base">
                  이용 내역 확인
                </strong>

              </div>


              <ChevronRight
                size={18}
                className="text-gray-300"
              />

            </button>

          </section>


          {/* =================================================
              Quick Menu
              ================================================= */}
          <section className="grid min-h-0 grid-rows-[auto_minmax(0,1fr)] rounded-3xl border border-gray-200 bg-white/90 p-3.5 shadow-xl shadow-gray-200/50 sm:p-4 lg:p-5">

            <div className="mb-3">

              <span className="text-[11px] font-black tracking-[0.12em] text-amber-700">
                QUICK MENU
              </span>


              <h2 className="mt-0.5 text-xl font-bold">
                자주 찾는 메뉴
              </h2>

            </div>


            <div className="grid min-h-0 grid-cols-2 auto-rows-[minmax(138px,1fr)] gap-2.5 md:grid-cols-4 md:gap-3">

              {menuItems.map(
                (
                  menu,
                ) => {
                  const Icon =
                    menu.icon;


                  const tone =
                    MENU_TONE_CLASSES[
                      menu.tone ??
                        'gray'
                    ];


                  return (
                    <button
                      key={
                        menu.id
                      }
                      type="button"
                      onClick={() =>
                        navigate(
                          menu.path,
                        )
                      }
                      className="group relative flex min-w-0 flex-col justify-between overflow-hidden rounded-2xl border border-gray-200 bg-white p-3.5 text-left transition hover:-translate-y-1 hover:border-gray-300 hover:shadow-xl sm:p-4"
                    >

                      <span
                        className={`pointer-events-none absolute -bottom-10 -right-8 h-28 w-28 rounded-full opacity-[0.08] ${tone.decoration}`}
                      />


                      <div className="relative flex items-center justify-between">

                        <span
                          className={`grid h-10 w-10 place-items-center rounded-xl ${tone.icon}`}
                        >

                          <Icon
                            size={22}
                            strokeWidth={
                              1.9
                            }
                          />

                        </span>


                        {menu.badge && (

                          <span
                            className={`rounded-full px-2 py-1 text-[9px] font-black ring-1 ring-inset ${tone.badge}`}
                          >
                            {
                              menu.badge
                            }
                          </span>

                        )}

                      </div>


                      <div className="relative mt-3 min-w-0 pr-5">

                        <strong className="block text-sm font-extrabold sm:text-base">
                          {
                            menu.label
                          }
                        </strong>


                        <small className="mt-1 line-clamp-2 block text-[11px] leading-4 text-gray-500 sm:text-xs">
                          {
                            menu.description
                          }
                        </small>

                      </div>


                      <ChevronRight
                        size={18}
                        className="absolute bottom-3.5 right-3.5 text-gray-300"
                      />

                    </button>
                  );
                },
              )}

            </div>

          </section>

        </section>

      </main>

    </>
  );
}